import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData, prefillFormData } from '../../utils/projectDataUtils';
import { printPermitForm, createCompanyHeader, formatFormField, formatTableRow } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const ProjectChecklistForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // General information
    date: '',
    jobNumber: '',
    
    // Bidding section
    bidding: {
      LEAD_UPLOADED_AND_SCHEDULED: { status: '', date: '' },
      JOB_WALK_SITE_VISIT_ARIEL_REPORT_PLANS: { status: '', date: '' },
      FOLLOW_UP_EMAIL_OR_CALL_TO_SAY_NICE_TO_MEET_YOU: { status: '', date: '' },
      BUILDING_TAKE_OFF_GOOGLE_EARTH_REVIEW_PLANS_OR_SKY_MEASURE_REPORT: { status: '', date: '' },
      GET_MATERIAL_QUOTE: { status: '', date: '' },
      CREATE_A_PROPOSAL: { status: '', date: '' },
      PROPOSAL_SENT: { status: '', date: '' },
      FOLLOW_UP_EMAIL_OR_CALL: { status: '', date: '' },
      biddingNotes: ''
    },
    // Job Load & Front End Loading section
    jobLoad: {
      CONTRACT_DOCUMENT_SIGNED: { status: '', date: '' },
      JOB_START_DEPOSIT_INVOICE_SENT_JOB_MOVE_TO_JOB_IN_BUILDER_TREND: { status: '', date: '' },
      PROJECT_DETAILS_REVIEWED_WITH_OPERATIONS: { status: '', date: '' },
      DEPOSIT_RECEIVED: { status: '', date: '' },
      PRE_CONSTRUCTION_SITE_WALK: { status: '', date: '' },
      PERMIT_REQUEST_FORM_FILLED_OUT_AND_SENT_IN: { status: '', date: '' },
      CITY_BUILDING_PERMIT_APPLICATION_SUBMITTED: { status: '', date: '' },
      WASTE_MANAGEMENT: { status: '', date: '' },
      SMOKE_AND_CARB_CERT_REQUIRED: { status: '', date: '' },
      CITY_BUILDING_PERMIT_RECEIVED: { status: '', date: '' },
      PROJECT_SCHEDULED_START_DATE: { status: '', date: '' },
      MATERIAL_ORDERED_DELIVERY_DATE: { status: '', date: '' },
      DEMO_SCHEDULED_DEMO_DATE: { status: '', date: '' },
      NOTIFICATIONS_MADE: { status: '', date: '' },
      jobLoadNotes: ''
    },
    // Project Field Operations section
    projectOperations: {
      MOBILIZATION_ROOF_TEAR_OFF: { status: '', date: '' },
      CITY_PERMIT_SHEETING_INSPECTION_SCHEDULED: { status: '', date: '' },
      CITY_PERMIT_SHEETING_INSPECTION_COMPLETE: { status: '', date: '' },
      PREP_DELIVERED: { status: '', date: '' },
      PRODUCT_DEMO_IN: { status: '', date: '' },
      PROGRESS_INVOICE_25: { status: '', date: '' },
      PROGRESS_INVOICE_PAID: { status: '', date: '' },
      ROOF_MATERIAL_DELIVERED: { status: '', date: '' },
      INSTALL_COMPLETE: { status: '', date: '' },
      JOB_WALK_AND_PUNCHLIST: { status: '', date: '' },
      CITY_PERMIT_FINAL_INSPECTION: { status: '', date: '' },
      projectOperationsNotes: ''
    },
    // Project Closeout section
    projectCloseout: {
      FINAL_JOB_WALK_WITH_ALL_PARTIES: { status: '', date: '' },
      FINAL_INVOICE_SENT: { status: '', date: '' },
      PERMIT_FEES_INCLUDED_CHANGE_ORDERS: { status: '', date: '' },
      FINAL_PAYMENT_RECEIVED: { status: '', date: '' },
      WARRANTY_CERTIFICATE_SENT: { status: '', date: '' },
      FOLLOW_UP_EMAIL_CALL_NOTE: { status: '', date: '' },
      ASK_FOR_REVIEW_OF_JOB_WELL_DONE: { status: '', date: '' },
      PROJECT_CLOSED_OUT_IN_BUILDER_TREND: { status: '', date: '' },
      projectCloseoutNotes: ''
    }
  });

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'projectChecklist') {
      setFormData(currentPermit.formData || formData);
    } else {
      // Fetch project data to prefill the form
      const getProjectData = async () => {
        try {
          // Get project data
          const projectData = await fetchProjectData(projectId);
          console.log('Project data for project checklist form:', projectData);
          
          // Handle the case where the project is nested in a 'project' property
          const actualProject = projectData.project || projectData;
          
          // Prefill the form data
          const prefilled = { ...formData };
          
          // Set today's date in MM/DD/YYYY format
          const now = new Date();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const year = now.getFullYear();
          prefilled.date = `${year}-${month}-${day}`;
          
          // Set job number
          if (actualProject.jobNumber) {
            prefilled.jobNumber = actualProject.jobNumber;
          } else if (actualProject.originalLead && actualProject.originalLead.jobNumber) {
            prefilled.jobNumber = actualProject.originalLead.jobNumber;
          }
          
          console.log('Prefilled project checklist form data:', prefilled);
          setFormData(prefilled);
        } catch (error) {
          console.error('Error prefilling project checklist form data:', error);
        }
      };
      
      getProjectData();
    }
  }, [currentPermit, projectId]);

  const handleChange = (section, field, subField, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: typeof prev[section][field] === 'object' && subField 
          ? { ...prev[section][field], [subField]: value }
          : value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData
          }
        })).unwrap();
        showToast('Project Checklist updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'projectChecklist',
          formData
        })).unwrap();
        showToast('Project Checklist saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Project Checklist', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderChecklistSection = (title, section, sectionData) => {
    const items = Object.entries(sectionData).filter(([key]) => key !== `${section}Notes`);
    
    return (
      <div className="mb-8">
        <div className="bg-gray-100 px-3 py-2 font-bold border border-gray-300 text-gray-800">
          {title}
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2 w-3/5 text-left">Item</th>
              <th className="border border-gray-300 p-2 w-1/5 text-center">Status</th>
              <th className="border border-gray-300 p-2 w-1/5 text-center">Date</th>
            </tr>
          </thead>
          <tbody>
            {items.map(([key, value]) => (
              <tr key={key}>
                <td className="border border-gray-300 p-2 text-sm">
                  {key.replace(/_/g, ' ')}
                </td>
                <td className="border border-gray-300 p-1 text-center">
                  <select
                    value={value.status}
                    onChange={(e) => handleChange(section, key, 'status', e.target.value)}
                    className="w-full p-1 border border-gray-200 rounded"
                  >
                    <option value="">Select</option>
                    <option value="completed">Completed</option>
                    <option value="pending">Pending</option>
                    <option value="na">N/A</option>
                  </select>
                </td>
                <td className="border border-gray-300 p-1 text-center">
                  <input
                    type="date"
                    value={value.date}
                    onChange={(e) => handleChange(section, key, 'date', e.target.value)}
                    className="w-full p-1 border border-gray-200 rounded"
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-2">
          <div className="font-medium text-sm mb-1">Notes:</div>
          <textarea
            value={sectionData[`${section}Notes`] || ''}
            onChange={(e) => handleChange(section, `${section}Notes`, null, e.target.value)}
            className="w-full border border-gray-300 p-2 h-20 text-sm"
            placeholder="Add notes here..."
          />
        </div>
      </div>
    );
  };

  const handleDownload = () => {
    // Create a printable version of the form content
    const formContent = `
      ${createCompanyHeader()}
      <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px;">
        <h1 class="form-title">PM CHECK LIST</h1>
        <div style="width: 200px;">
          ${formatFormField('Date', formData.date)}
          ${formatFormField('Job Number', formData.jobNumber)}
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">BIDDING</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.bidding)
              .filter(([key]) => key !== 'biddingNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.bidding.biddingNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">JOB LOAD & FRONT END LOADING (FEL)</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.jobLoad)
              .filter(([key]) => key !== 'jobLoadNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.jobLoad.jobLoadNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">PROJECT FIELD OPERATIONS</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.projectOperations)
              .filter(([key]) => key !== 'projectOperationsNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.projectOperations.projectOperationsNotes || ''}</div>
        </div>
      </div>
      
      <div class="form-section">
        <h2 class="form-subtitle">PROJECT CLOSEOUT</h2>
        <table class="form-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Status</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            ${Object.entries(formData.projectCloseout)
              .filter(([key]) => key !== 'projectCloseoutNotes')
              .map(([key, value]) => `
                <tr>
                  <td>${key.replace(/_/g, ' ')}</td>
                  <td>${value.status || ''}</td>
                  <td>${value.date || ''}</td>
                </tr>
              `).join('')}
          </tbody>
        </table>
        <div class="form-notes">
          <strong>Notes:</strong>
          <div>${formData.projectCloseout.projectCloseoutNotes || ''}</div>
        </div>
      </div>
    `;
    
    // Print the form
    printPermitForm(formData, 'PM Check List', formContent);
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img 
            src="/logo/JRT-logo-small.jpg" 
            alt="JRT Roofing & Waterproofing" 
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">JRT ROOFING & WATERPROOFING</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>P.O. Box 3688</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 742-2892</p>
          <p className="uppercase">JRTROOFING.COM</p>
          <p>CA 1070037</p>
        </div>
      </div>

      <div className="flex justify-between items-start mb-6">
        <h1 className="text-2xl font-bold">PM CHECK LIST</h1>
        <div className="flex flex-col space-y-2 w-64">
          <div className="flex items-center justify-between">
            <label className="font-medium">Date:</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
          <div className="flex items-center justify-between">
            <label className="font-medium">Job Number:</label>
            <input
              type="text"
              name="jobNumber"
              value={formData.jobNumber}
              onChange={(e) => setFormData({...formData, jobNumber: e.target.value})}
              className="w-40 p-1 border border-gray-300"
            />
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {renderChecklistSection('BIDDING', 'bidding', formData.bidding)}
        {renderChecklistSection('JOB LOAD & FRONT END LOADING (FEL)', 'jobLoad', formData.jobLoad)}
        {renderChecklistSection('PROJECT FIELD OPERATIONS', 'projectOperations', formData.projectOperations)}
        {renderChecklistSection('PROJECT CLOSEOUT', 'projectCloseout', formData.projectCloseout)}

        {/* Add padding at the bottom to ensure content isn't hidden behind the floating bar */}
        <div className="pb-20"></div>
      </form>
      
      {/* Floating Action Bar */}
      <FloatingActionBar 
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default ProjectChecklistForm;
